import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AiGeneratedFlag,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfidenceBadge,
  EmptyState,
  Select,
  StudioState,
  formatDate,
} from "@odingard/design-system";
import type {
  AttentionGrouping,
  AttentionPrincipal,
  AttentionWorkItem,
  AttentionWorkItems,
} from "@odingard/sdk-client";
import { APPLICATION_MANIFESTS } from "../../shell/applications";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, attentionApi } from "../../lib/api";

const GROUPINGS: { value: AttentionGrouping; label: string }[] = [
  { value: "application", label: "Application" },
  { value: "priority", label: "Priority" },
  { value: "due", label: "Due date" },
];

/**
 * An application named as it names itself. The projection carries the platform's
 * key for it, which is not what a customer calls it.
 */
function appLabel(application: string): string {
  const declared = APPLICATION_MANIFESTS.find(
    (manifest) => manifest.id === application.replaceAll("_", "-"),
  );
  return (
    declared?.name ??
    application.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}

/**
 * Names the person an item is addressed to. The platform addresses them by the
 * principal they act as and states the Studio id behind it, because only Studio
 * holds the name.
 */
function ownerName(
  principal: AttentionPrincipal,
  members: { id: string; displayName: string; email: string }[],
): string {
  const member = members.find(
    (candidate) =>
      candidate.id === principal.externalPrincipalId ||
      candidate.id === principal.principalId,
  );
  return member ? member.displayName || member.email : "No name available";
}

function itemApplication(item: AttentionWorkItem): string {
  if (item.route.startsWith("/studio/")) return item.route.split("/")[2] ?? "studio";
  const separator = item.route.indexOf(":");
  return separator >= 0 ? item.route.slice(0, separator).replaceAll("_", "-") : "studio";
}

function itemRoute(item: AttentionWorkItem): string {
  if (item.route.startsWith("/studio/")) return item.route;
  const separator = item.route.indexOf(":");
  if (separator < 0) return "/studio";
  const application = item.route.slice(0, separator).replaceAll("_", "-");
  const path = item.route.slice(separator + 1);
  return `/studio/${application}${path.startsWith("/") ? path : `/${path}`}`;
}

function dueBucket(item: AttentionWorkItem): string {
  if (!item.dueAt) return "No due date";
  const due = new Date(item.dueAt);
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  if (dueDay < startOfToday) return "Overdue";
  if (dueDay.getTime() === startOfToday.getTime()) return "Today";
  const endOfWeek = new Date(startOfToday);
  endOfWeek.setDate(startOfToday.getDate() + (6 - startOfToday.getDay()));
  return dueDay <= endOfWeek ? "This week" : "Later";
}

const PRIORITY_ORDER = ["critical", "high", "normal", "low"];
const DUE_ORDER = ["Overdue", "Today", "This week", "Later", "No due date"];

function groupItems(items: AttentionWorkItem[], grouping: AttentionGrouping): [string, AttentionWorkItem[]][] {
  const groups = new Map<string, AttentionWorkItem[]>();
  for (const item of [...items].sort((left, right) => left.title.localeCompare(right.title) || left.id.localeCompare(right.id))) {
    const key =
      grouping === "priority"
        ? item.priority
        : grouping === "due"
          ? dueBucket(item)
          : appLabel(itemApplication(item));
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  const order = grouping === "priority"
    ? PRIORITY_ORDER
    : grouping === "due"
      ? DUE_ORDER
      : [...groups.keys()].sort((left, right) => left.localeCompare(right));
  return order.filter((key) => groups.has(key)).map((key) => [key, groups.get(key)!]);
}

function WithheldNotice({ withheld }: { withheld: { not_entitled: number; permission_denied: number; permission_unresolvable: number } }) {
  const total = withheld.not_entitled + withheld.permission_denied + withheld.permission_unresolvable;
  if (total === 0) return null;
  const reasons: string[] = [];
  if (withheld.not_entitled > 0) reasons.push("an application your organization is not entitled to use");
  if (withheld.permission_denied + withheld.permission_unresolvable > 0) reasons.push("work your current access does not allow you to see");
  return (
    <div className="ods-failclosed" role="status">
      {total} other {total === 1 ? "item is" : "items are"} not shown because of {reasons.join(" and ")}.
    </div>
  );
}

function WorkItemCard({
  item,
  members,
  busy,
  onPresentation,
}: {
  item: AttentionWorkItem;
  members: { id: string; displayName: string; email: string }[];
  busy: boolean;
  onPresentation: (item: AttentionWorkItem, input: Parameters<typeof attentionApi.updatePresentation>[2]) => void;
}) {
  const owner = ownerName(item.authoritativeOwner, members);
  const subject = item.subject;
  return (
    <Card>
      <CardHeader
        title={
          <div>
            <h3 style={{ margin: 0 }}>{item.title}</h3>
            <p className="muted-note" style={{ margin: "var(--ods-space-1) 0 0" }}>
              {appLabel(itemApplication(item))} · {item.workItemType}
            </p>
          </div>
        }
      />
      <CardBody>
        <div className="ods-stack" style={{ gap: "var(--ods-space-2)" }}>
          <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
            <Badge tone="neutral">Priority: {item.priority}</Badge>
            {item.risk ? <Badge tone={item.risk.toLowerCase() === "high" ? "danger" : "warning"}>Risk: {item.risk}</Badge> : null}
            {item.basis === "derived" ? (
              <>
                <AiGeneratedFlag />
                {typeof item.confidence === "number" ? <ConfidenceBadge value={item.confidence} /> : null}
              </>
            ) : <Badge tone="neutral">Asserted</Badge>}
            {item.stale === true ? <Badge tone="warning">Stale source</Badge> : null}
            {item.presentation.isDismissed ? <Badge tone="warning">Hidden for me</Badge> : null}
            {item.presentation.snoozedUntil ? <Badge tone="warning">Snoozed until {formatDate(item.presentation.snoozedUntil)}</Badge> : null}
          </div>
          <dl className="ods-detail-list">
            <dt>Authoritative owner</dt>
            <dd>{owner}</dd>
            <dt>Due date</dt>
            <dd>
              {item.dueAt ? `${formatDate(item.dueAt)} · ${item.dueDateAuthority ?? "Source application"}` : "No due date"}
              {item.dueDateReference ? ` (${item.dueDateReference})` : ""}
            </dd>
          </dl>
          {item.relationshipContext.length > 0 ? (
            <Link to={`/studio/context/${encodeURIComponent(subject.type)}/${encodeURIComponent(subject.id)}`}>
              View related context
            </Link>
          ) : null}
          <div className="ods-row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
            <Link to={itemRoute(item)}>
              Open in {appLabel(itemApplication(item))}
            </Link>
            <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onPresentation(item, { isRead: !item.presentation.isRead })}
              >
                {item.presentation.isRead ? "Mark unread" : "Mark read"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onPresentation(item, { isPinned: !item.presentation.isPinned })}
              >
                {item.presentation.isPinned ? "Unpin" : "Pin"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onPresentation(item, { isDismissed: !item.presentation.isDismissed })}
              >
                {item.presentation.isDismissed ? "Show for me" : "Hide for me"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onPresentation(item, {
                  snoozedUntil: item.presentation.snoozedUntil
                    ? null
                    : new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
                })}
              >
                {item.presentation.snoozedUntil ? "End snooze" : "Snooze for one day"}
              </Button>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export function AttentionCenter() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [busyId, setBusyId] = useState<string | null>(null);
  const [workItemType, setWorkItemType] = useState("");
  const [filterState, setFilterState] = useState("");
  const [includePersonalized, setIncludePersonalized] = useState(false);
  const preferencesState = useAsync(
    () => attentionApi.preferences(orgId),
    [orgId],
  );
  const optionsState = useAsync(
    () => attentionApi.workItems(orgId, {
      includeDismissed: includePersonalized,
      includeSnoozed: includePersonalized,
    }),
    [orgId, includePersonalized],
  );
  const membersState = useAsync(
    () => api.members(orgId),
    [orgId],
  );
  const grouping =
    preferencesState.data?.data && "preferences" in preferencesState.data.data
      ? preferencesState.data.data.preferences.grouping
      : "application";
  const itemsState = useAsync(
    () => attentionApi.workItems(orgId, {
      includeDismissed: includePersonalized,
      includeSnoozed: includePersonalized,
      grouping,
      ...(workItemType ? { workItemType } : {}),
      ...(filterState ? { state: filterState } : {}),
    }),
    [orgId, grouping, workItemType, filterState, includePersonalized],
  );
  const itemData = itemsState.data?.data;
  const items = itemData && "workItems" in itemData ? itemData : undefined;
  const optionData = optionsState.data?.data;
  const optionItems: AttentionWorkItems | undefined =
    optionData && "workItems" in optionData ? optionData : items;
  const unavailable =
    itemsState.data?.mode === "unavailable" ||
    (itemData && "state" in itemData && itemData.state === "unavailable");

  async function changeGrouping(next: AttentionGrouping) {
    const preferences = preferencesState.data;
    if (!preferences || next === grouping) return;
    if (!("preferences" in preferences.data)) return;
    await attentionApi.putPreferences(orgId, {
      ...preferences.data.preferences,
      grouping: next,
    });
    await preferencesState.reload();
  }

  async function updatePresentation(item: AttentionWorkItem, input: Parameters<typeof attentionApi.updatePresentation>[2]) {
    setBusyId(item.id);
    try {
      await attentionApi.updatePresentation(orgId, item.id, input);
      await itemsState.reload();
    } finally {
      setBusyId(null);
    }
  }

  const error = preferencesState.error ?? itemsState.error ?? optionsState.error ?? membersState.error;
  const reload = () => {
    preferencesState.reload();
    optionsState.reload();
    membersState.reload();
    itemsState.reload();
  };
  if (itemsState.loading && !itemsState.data) return <StudioState state="loading" description="Loading your attention queue…" />;
  if (error) {
    const stateName = error.status === 401 || error.status === 403 ? "unauthorized" : "error";
    return <StudioState state={stateName} description={error.message} onRetry={reload} />;
  }
  if (preferencesState.data?.mode === "unavailable" || unavailable) {
    return <StudioState state="dependency_unavailable" title="Attention Center unavailable" description="We could not ask the shared Attention Center. Nothing has been lost; try again when the service returns." onRetry={reload} />;
  }
  if (!items || "state" in items) {
    return <StudioState state="dependency_unavailable" title="Attention Center unavailable" onRetry={reload} />;
  }
  if (items.workItems.length === 0) {
    return (
      <>
        <div className="page-head">
          <div><h1>Attention Center</h1><p>Work published across your Studio applications.</p></div>
        </div>
        <WithheldNotice withheld={items.withheld} />
        <Card><CardBody><EmptyState title="Nothing needs your attention" description="The shared queue has no visible work for you right now." /></CardBody></Card>
      </>
    );
  }
  return (
    <>
      <div className="page-head">
        <div><h1>Attention Center</h1><p>Work published across your Studio applications.</p></div>
      </div>
      <Card>
        <CardBody>
          <div className="ods-row" style={{ alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap" }}>
            <label>
              <span className="muted-note">Group by</span>
              <Select aria-label="Group attention items by" value={grouping} onChange={(event) => void changeGrouping(event.target.value as AttentionGrouping)}>
                {GROUPINGS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Select>
            </label>
            <label>
              <span className="muted-note">Filter by work-item type</span>
              <Select aria-label="Filter attention items by work-item type" value={workItemType} onChange={(event) => setWorkItemType(event.target.value)}>
                <option value="">All types</option>
                {[...new Set(optionItems?.workItems.map((item) => item.workItemType) ?? [])].sort().map((type) => <option key={type} value={type}>{type}</option>)}
              </Select>
            </label>
            <label>
              <span className="muted-note">Filter by state</span>
              <Select aria-label="Filter attention items by state" value={filterState} onChange={(event) => setFilterState(event.target.value)}>
                <option value="">All states</option>
                {[...new Set(optionItems?.workItems.map((item) => item.state) ?? [])].sort().map((itemState) => <option key={itemState} value={itemState}>{itemState}</option>)}
              </Select>
            </label>
            <label>
              <input
                type="checkbox"
                checked={includePersonalized}
                onChange={(event) => setIncludePersonalized(event.target.checked)}
              />
              {" "}Show hidden and snoozed for me
            </label>
            <span className="muted-note">{items.workItems.length} visible {items.workItems.length === 1 ? "item" : "items"}</span>
          </div>
        </CardBody>
      </Card>
      <WithheldNotice withheld={items.withheld} />
      {groupItems(items.workItems, grouping).map(([name, grouped]) => (
        <section key={name} className="section" aria-labelledby={`attention-group-${name}`}>
          <h2 id={`attention-group-${name}`}>{name}</h2>
          <div className="ods-stack">
            {grouped.map((item) => <WorkItemCard key={item.id} item={item} members={membersState.data?.members ?? []} busy={busyId === item.id} onPresentation={(entry, input) => void updatePresentation(entry, input)} />)}
          </div>
        </section>
      ))}
    </>
  );
}
